import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '@core/auth/store';
import { useCanWrite } from '@core/auth/access';
import { data, useTable } from '@core/store';
import { paletteColorOf } from '@core/palette';
import { dateTimeLabel } from '@core/dates';
import { activePeople } from '@core/people';
import { Avatar } from '@core/ui/Avatar';
import { Button } from '@core/ui/Button';
import { fieldInput as input } from '@core/ui/form';
import { cn } from '@core/ui/cn';
import { isProblem, planQuickPerson, similarPeople } from '../../admin/planner';
import { answerQuestion, askQuestion, askedBy, authorOf, closeQuestion, editAnswerText, editQuestionText, removeQuestion, reopenQuestion, type Question } from './questions';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { EntitySelect } from '@core/ui/EntitySelect';
import { confirmDialog } from '@core/ui/confirm';
import { CircleCheck, MessageCircleReply, Pencil, RotateCcw, SquarePen, Trash2 } from 'lucide-react';

const when = dateTimeLabel;

function usePeopleOptions() {
  const people = useTable('person');
  return useMemo(() => activePeople(people.values()), [people]);
}

/**
 * «От чьего имени»: общий выбор человека с поиском; пусто — «me», человек моего аккаунта. Незнакомое имя
 * заводится прямо здесь: он не назначаемый, пока админ не решит иначе — то же правило в базе.
 */
function AuthorSelect({ value, onChange, label }: { value: string | null; onChange: (v: string | null) => void; label: string }) {
  const options = usePeopleOptions();
  const canWrite = useCanWrite();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const add = async (typed: string, close: () => void) => {
    const planned = planQuickPerson(typed, paletteColorOf(typed), options);
    if (isProblem(planned)) { setError(planned.error); return; }
    const insert = planned.changes[0] as { insert: Record<string, unknown> };
    const id = crypto.randomUUID();
    setBusy(true);
    const r = await data.applyChanges([{ table: 'person', insert: { ...insert.insert, id } }], planned.op);
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    onChange(id);
    close();
  };
  return (
    <>
      <EntitySelect
        label={label} size="sm" className="w-44" value={value} empty="me" placeholder="me"
        options={options.map((p) => ({ id: p.id, name: p.name, color: p.color, person: true }))}
        onChange={onChange}
        footer={(typed, close) => {
          const known = options.some((p) => p.name.toLowerCase() === typed.toLowerCase());
          if (typed.length < 2 || known || !canWrite) return null;
          return (
            <div className="border-t border-gray-200 mt-1 pt-1 px-1 flex items-center gap-2 text-xs text-ink-muted">
              <button type="button" className="min-h-0 px-2 py-1 rounded border border-dashed border-accent/60 text-accent font-medium disabled:opacity-50"
                data-add-person disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={() => void add(typed, close)}>+ Add «{typed}»</button>
              {(() => { const similar = similarPeople(typed, options, 3); return similar.length > 0 && <span>similar: {similar.map((p) => p.name).join(', ')}</span>; })()}
            </div>
          );
        }}
      />
      {error && <span role="alert" className="text-danger text-xs font-medium">{error}</span>}
    </>
  );
}

const CLAMP_LINES = 3;

/** Текст с карандашом: правка на месте, пишется как любое поле — в журнал. Длинный текст свёрнут до трёх строк с «Show all». */
function EditableText({ text, className, onSave, editSignal = 0 }: { text: string; className?: string; onSave: (t: string) => Promise<{ ok: boolean; error?: string }>; editSignal?: number }) {
  const [editing, setEditing] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  // из меню: после закрытия меню фокус возвращает себе окно — забираем его в поле чуть позже
  useEffect(() => { if (editSignal > 0) { setValue(text); setEditing(true); const t = setTimeout(() => area.current?.focus(), 60); return () => clearTimeout(t); } return undefined; }, [editSignal]);
  const [value, setValue] = useState(text);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || editing) return;
    setOverflows(expanded ? overflows : el.scrollHeight > el.clientHeight + 1);
  }, [text, editing, expanded]);
  if (!editing) {
    return (
      <span className={cn('group/t flex-1 min-w-0 flex flex-col', className)}>
        <span className="flex items-start gap-1">
          <span ref={ref} className={cn('whitespace-pre-wrap min-w-0', !expanded && `line-clamp-${CLAMP_LINES}`)}>{text}</span>
          <button type="button" className="min-h-0 px-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 opacity-0 group-hover/t:opacity-100 focus:opacity-100 text-xs shrink-0" title="Edit" aria-label="Edit text" onClick={() => { setValue(text); setEditing(true); }}>✎</button>
        </span>
        {(overflows || expanded) && (
          <button type="button" className="min-h-0 self-start text-xs text-accent hover:underline font-normal" data-show-all onClick={() => setExpanded(!expanded)}>{expanded ? 'Show less' : 'Show all'}</button>
        )}
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-1 flex-1" data-keeps-escape>{/* Escape закрывает правку, не карточку */}
      <textarea ref={area} className={input} rows={2} value={value} autoFocus aria-label="Edit text" onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }} />
      <span className="flex gap-2">
        <Button className="min-h-0 px-2 py-1 text-sm" disabled={!value.trim()} onClick={() => void onSave(value).then((r) => { if (r.ok) setEditing(false); else setError(r.error ?? 'Failed'); })}>Save</Button>
        <Button variant="ghost" className="min-h-0 px-2 py-1 text-sm" onClick={() => setEditing(false)}>Cancel</Button>
        {error && <span role="alert" className="text-danger text-sm">{error}</span>}
      </span>
    </span>
  );
}

/** Один вопрос с ответами и формой ответа. compact — для повестки (форма свёрнута). */
export function QuestionItem({ q, compact = false, onOpenTask }: { q: Question; compact?: boolean; onOpenTask?: () => void }) {
  const people = useTable('person');
  const profiles = useTable('profile');
  const answers = useTable('answer');
  const me = useAuth((s) => s.profile);
  const [text, setText] = useState('');
  const [author, setAuthor] = useState<string | null>(null);
  const [open, setOpen] = useState(!compact);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editSignal, setEditSignal] = useState(0);
  const canWrite = useCanWrite();
  const mine = useMemo(() => [...answers.values()].filter((a) => a.question_id === q.id).sort((a, b) => a.created_at.localeCompare(b.created_at)), [answers, q.id]);
  const authorId = author ?? me?.person_id ?? null;

  const run = async (fn: () => Promise<{ ok: boolean; error?: string }>) => {
    setBusy(true); setError(null);
    const r = await fn();
    setBusy(false);
    if (!r.ok) setError(r.error ?? 'Failed'); else { setText(''); if (compact) setOpen(false); }
  };
  const submit = (close: boolean) => (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || !me) return;
    void run(() => answerQuestion(q, text, authorId, close, me.id));
  };

  // автор и дата — своим цветом и с кружком человека, чтобы глаз отделял их от текста вопроса
  const meta = 'ml-auto text-xs text-accent-strong whitespace-nowrap';
  const who = (personId: string | null, recordedBy: string, size: number) => { const a = authorOf(personId, recordedBy, people, profiles); return <Avatar name={a.name} color={a.color} src={a.avatar} size={size} className="mt-0.5" />; };
  // меню вопроса: те же действия, что кнопки, одним списком; удаление — с вопросом
  const menu: MenuItem[] = [
    ...(onOpenTask ? [{ label: 'Open the task', onSelect: onOpenTask, icon: SquarePen }] : []),
    ...(q.status === 'open' && !open ? [{ label: 'Answer', onSelect: () => setOpen(true), icon: MessageCircleReply }] : []),
    ...(q.status === 'open' && me ? [{ label: 'Resolve without answer', onSelect: () => void run(() => closeQuestion(q, me.id)), icon: CircleCheck }] : []),
    ...(q.status === 'closed' ? [{ label: 'Reopen', onSelect: () => void run(() => reopenQuestion(q)), icon: RotateCcw }] : []),
    { label: 'Edit text', onSelect: () => setEditSignal((n) => n + 1), icon: Pencil },
    'sep',
    { label: 'Delete question', danger: true, icon: Trash2, onSelect: () => void (async () => {
      if (!(await confirmDialog({ title: 'Delete question', text: <>Delete the question <b>«{q.text.slice(0, 80)}{q.text.length > 80 ? '…' : ''}»</b> with its answers? It stays in the history.</>, yes: 'Delete', danger: true }))) return;
      const r = await removeQuestion(q);
      if (!r.ok) setError(r.error ?? 'Failed');
    })() },
  ];
  const box = (
    <div className={cn('rounded-md border px-3 py-2', q.status === 'open' ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50')} data-question={q.id}>
      <div className="flex items-start gap-2">
        {who(q.author_person_id, q.recorded_by, 20)}
        <EditableText text={q.text} className="font-medium" onSave={(t) => editQuestionText(q, t)} editSignal={editSignal} />
        <span className={meta}>{askedBy(q.author_person_id, q.recorded_by, people, profiles)} · {when(q.created_at)}</span>
      </div>
      {mine.map((a) => (
        <div key={a.id} className="mt-2 pl-3 border-l-2 border-gray-300 flex items-start gap-2">
          {who(a.author_person_id, a.recorded_by, 18)}
          <EditableText text={a.text} onSave={(t) => editAnswerText(a, t)} />
          <span className={meta}>{askedBy(a.author_person_id, a.recorded_by, people, profiles)} · {when(a.created_at)}</span>
        </div>
      ))}
      {q.status === 'closed' && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
          Resolved {q.closed_at ? when(q.closed_at) : ''}{q.closed_by ? ` by ${askedBy(null, q.closed_by, people, profiles)}` : ''}
          <Button variant="ghost" className="min-h-0 px-2 py-1 text-sm ml-auto" disabled={busy} onClick={() => void run(() => reopenQuestion(q))}>Reopen</Button>
        </div>
      )}
      {q.status === 'open' && !open && (
        <div className="mt-2"><Button variant="secondary" className="min-h-0 px-2 py-1 text-sm" onClick={() => setOpen(true)}>Answer</Button></div>
      )}
      {q.status === 'open' && open && (
        <form onSubmit={submit(true)} className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm text-gray-600"><AuthorSelect value={author} onChange={setAuthor} label="Answered by" /> answering:</div>
          <textarea className={input} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Answer…" aria-label="Answer" autoFocus={compact} />
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" className="min-h-0 px-2 py-1 text-sm text-gray-600" disabled={busy || !me} onClick={() => me && void run(() => closeQuestion(q, me.id))} title="Mark resolved without writing an answer">Resolve without answer</Button>
            <span className="ml-auto" />
            <Button variant="secondary" className="min-h-0 px-2 py-1 text-sm" disabled={busy || !text.trim()} onClick={(e) => { e.preventDefault(); if (me) void run(() => answerQuestion(q, text, authorId, false, me.id)); }}>Answer, keep open</Button>
            <Button type="submit" className="min-h-0 px-3 py-1 text-sm" disabled={busy || !text.trim()}>Answer & resolve</Button>
          </div>
          {error && <p role="alert" className="text-danger text-sm font-medium">{error}</p>}
        </form>
      )}
    </div>
  );
  return canWrite ? <Menu items={menu}>{box}</Menu> : box;
}

/** Вкладка карточки задачи: открытые, форма нового вопроса, закрытые свёрнуты. active — вкладка видна: фокус в поле вопроса. */
export function TaskQuestions({ taskId, active }: { taskId: string; active: boolean }) {
  const questions = useTable('question');
  const ask = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (active) ask.current?.focus(); }, [active]);
  const me = useAuth((s) => s.profile);
  const [text, setText] = useState('');
  const [author, setAuthor] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useMemo(() => [...questions.values()].filter((q) => q.task_id === taskId).sort((a, b) => a.created_at.localeCompare(b.created_at)), [questions, taskId]);
  const open = list.filter((q) => q.status === 'open');
  const closed = list.filter((q) => q.status === 'closed');

  async function submitAsk(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    const r = await askQuestion(taskId, text, author ?? me?.person_id ?? null);
    if (!r.ok) setError(r.error ?? 'Failed'); else { setText(''); setError(null); }
  }

  return (
    <div className="flex flex-col gap-3">
      <form onSubmit={(e) => void submitAsk(e)} className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-sm text-gray-600"><AuthorSelect value={author} onChange={setAuthor} label="Asked by" /> asking:</div>
        <textarea ref={ask} className={input} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask a question about this task…" aria-label="New question" />
        <div className="flex items-center gap-2">
          <Button type="submit" className="ml-auto min-h-0 px-3 py-1 text-sm" disabled={!text.trim()}>Ask</Button>
        </div>
        {error && <p role="alert" className="text-danger text-sm font-medium">{error}</p>}
      </form>
      {open.map((q) => <QuestionItem key={q.id} q={q} />)}
      {open.length === 0 && <p className="text-gray-500 text-sm">No open questions.</p>}
      {closed.length > 0 && (
        <button type="button" className="min-h-0 self-start text-sm text-accent underline" onClick={() => setShowClosed(!showClosed)}>
          {showClosed ? 'Hide' : 'Show'} {closed.length} resolved
        </button>
      )}
      {showClosed && closed.map((q) => <QuestionItem key={q.id} q={q} />)}
    </div>
  );
}
